"use client";

import type React from "react";
import ListingCardSmall, {
  type ListingCardSmallProps,
} from "@/features/listings/components/ListingCard_Small";
import ApplicationModeBadge from "@/features/listings/components/ApplicationModeBadge";
import CampaignCardMark from "@/features/campaigns/components/CampaignCardMark";
import type { ListingCardDTO } from "@/types/listing";

type ListingCardFromDTOProps = {
  listing: ListingCardDTO;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string, isFav: boolean) => void;
} & Pick<
  ListingCardSmallProps,
  | "variant"
  | "imagePriority"
  | "onHoverChange"
  | "footerContent"
  | "showFavoriteButton"
  | "imageOverlayContent"
  | "imageTopRightContent"
  | "imageTopLeftContent"
  | "showHostLogo"
  | "contentTopRightContent"
  | "imageBottomLeftContent"
>;

const ListingCardFromDTO: React.FC<ListingCardFromDTOProps> = ({
  listing,
  isFavorite,
  onFavoriteToggle,
  imageTopLeftContent,
  ...cardProps
}) => {
  const campaigns = listing.campaigns;
  // `listing.rent` is always the ordinary rent; the campaign carries the
  // restated pair. Show the effective figure as the price and the ordinary
  // one struck through — never the other way round.
  const displayRent = campaigns?.effectiveRent ?? listing.rent ?? 0;

  return (
    <ListingCardSmall
      id={listing.id}
      title={listing.title}
      location={listing.location}
      rooms={listing.rooms || 0}
      sizeM2={listing.sizeM2 || 0}
      rent={displayRent}
      landlordType={listing.hostType}
      hostName={listing.hostName}
      hostLogoUrl={listing.hostLogoUrl}
      isVerified={listing.verifiedHost}
      isFavorite={isFavorite}
      onFavoriteToggle={onFavoriteToggle}
      imageUrl={listing.imageUrl}
      // The campaign price, when a campaign restates it. `rent` is already the
      // discounted figure from the backend — the card never does the arithmetic.
      originalRent={campaigns?.originalRent}
      // One campaign line, at the price, whatever the offer is. A discount's
      // period note wins ("Rabatterat pris 2026"); an offer that leaves the
      // price alone shows its label there instead ("Resekort ingår"). One line
      // is the budget — a campaign card that grows three lines taller than its
      // neighbours reads as broken, not informative.
      priceNote={campaigns?.priceNote ?? campaigns?.badge?.label}
      // One row of plain white tags, top left (the heart owns the top right).
      // Express and Kampanj share one style so a card carrying both stays calm.
      // Queue-required homes carry no tag: they are the ordinary case, and
      // marking them all would mark nothing.
      imageTopLeftContent={
        imageTopLeftContent ?? (
          <div className="flex flex-wrap gap-1.5">
            <ApplicationModeBadge mode={listing.applicationMode} />
            <CampaignCardMark badge={campaigns?.badge} />
          </div>
        )
      }
      href={`/housing/${encodeURIComponent(listing.id)}`}
      {...cardProps}
    />
  );
};

export default ListingCardFromDTO;
